import GlobalGridStateTilesAndResources from '../../GridState/GlobalGridStateTilesAndResources';
import NPCsInGridManager from '../../GridState/GridStateNPCs';
import playersInGridManager from '../../GridState/PlayersInGrid';
import soundManager from '../../Sound/SoundManager';
import CombatFX from '../../Render/PixiRenderer/CombatFX';
import { createImpactEffect } from '../../VFX/VFX';
import { isWallBlocking } from '../../Utils/GridManagement';

/**
 * Sight vs. a clear swing (Utils/GridManagement.isWallBlocking, one line-of-sight rule for the
 * whole game). Walls and doors block both. Trees only block the swing: an enemy still notices
 * and chases you past a tree, but cannot hit you through one, the same as you cannot hit it.
 */
function canSeeTarget(npcPosition, targetPosition) {
    return !isWallBlocking(npcPosition, targetPosition);
}
function canHitTarget(npcPosition, targetPosition) {
    return !isWallBlocking(npcPosition, targetPosition, { trees: true });
}

const updateThisNPC = async function(gridId) {
  await NPCsInGridManager.updateNPC(gridId, this.id, {
    state: this.state,
    position: this.position,
    homeX: this.homeX,
    homeY: this.homeY,
  });
};

/**
 * Track 3b (docs/audits/combat-and-npc-review-2026-10-07.md): an Enemy roams inside a LEASH
 * around a HOME tile, and the home MOVES. The first home is where the enemy first stands (its
 * layout tile, or the Spawner's spawn tile). When it gives up on the player (sight lost, the
 * chase times out, the player dies, camps or leaves) it does not walk back: it re-anchors
 * where it is and roams there, so a player can lure enemies away from a corridor, a spawner or
 * a door, and a dungeon slowly rearranges itself (it resets lazily anyway). Home rides on the
 * NPC record (homeX / homeY), like a Citizen's. No maximum drift, no drifting back (owner's
 * open points, defaulted 2026-10-09).
 */
const ENEMY_LEASH = 4; // tiles; a template may override with `leashradius` in resources.json

/**
 * The leap (the Ogre; any enemy with `leapcooldown` > 0 in resources.json, owner 2026-10-15):
 * chasing a player who is far off, with a clear line (no walls, doors or trees) to a free tile
 * beside them, it stops, vibrates for LEAP_CHARGE_MS, then leaps there in LEAP_MS. Not again
 * for `leapcooldown` seconds after landing.
 */
const LEAP_CHARGE_MS = 1000;
const LEAP_MS = 280;
const LEAP_MIN_REACH = 3;    // closer than this it just walks
const LEAP_MAX_REACH = 10;   // farther than this it walks closer first

/** The free tile beside the player the enemy can leap to in a straight, clear line, or null. */
function leapLanding(npc, target, tiles, resources, npcs) {
  const from = { x: Math.floor(npc.position.x), y: Math.floor(npc.position.y) };
  const px = Math.floor(target.position.x), py = Math.floor(target.position.y);
  const options = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue;
      const t = { x: px + dx, y: py + dy };
      if (!npc.isValidTile(t.x, t.y, tiles, resources, npcs)) continue;
      if (isWallBlocking(from, t, { trees: true })) continue;   // walls, doors and trees stop a leap
      options.push({ ...t, d: (t.x - from.x) ** 2 + (t.y - from.y) ** 2 });
    }
  }
  options.sort((a, b) => a.d - b.d);   // the near side of the player
  return options[0] || null;
}

/** Start a leap if this enemy can and should; true when it did (the tick is spent). */
function tryLeap(npc, gridId, tiles, resources, npcs) {
  const cooldownMs = Number(npc.leapcooldown) * 1000;
  if (!(cooldownMs > 0) || npc.leaping || Date.now() < (npc.nextLeapAt || 0) || !npc.targetPC) return false;
  const far = reach(npc.position, npc.targetPC.position);
  if (far < LEAP_MIN_REACH || far > LEAP_MAX_REACH) return false;
  if (!leapLanding(npc, npc.targetPC, tiles, resources, npcs)) return false;

  npc.leaping = true;
  npc.path = null;
  npc.nextUpdateAt = Date.now() + LEAP_CHARGE_MS + LEAP_MS;   // its own clock waits out the leap
  CombatFX.enemyLeapCharge(npc.id, LEAP_CHARGE_MS);
  setTimeout(async () => {
    try {
      // the player moved during the wind-up: aim at where they are now, if still clear
      const live = Object.values(playersInGridManager.getPlayersInGrid(gridId) || {})[0];
      const liveNpcs = Object.values(NPCsInGridManager.getNPCsInGrid(gridId) || {});
      const landing = live && live.hp > 0
        ? leapLanding(npc, live, GlobalGridStateTilesAndResources.getTiles(), GlobalGridStateTilesAndResources.getResources(), liveNpcs)
        : null;
      if (!landing) return;
      if (landing.x !== Math.floor(npc.position.x)) npc.facing = landing.x > npc.position.x ? 1 : -1;
      npc.position.x = landing.x;
      npc.position.y = landing.y;
      npc.lastStepMs = LEAP_MS;          // the renderer tweens the whole leap in this time
      npc.stepStartedAt = Date.now();
      NPCsInGridManager.updateNPCPosition(npc.gridId || gridId, npc.id, { x: landing.x, y: landing.y });
      CombatFX.enemyLeapArc(npc.id, LEAP_MS);
    } catch (err) {
      console.error('Enemy leap failed:', err);
    } finally {
      setTimeout(() => {
        npc.leaping = false;
        npc.nextLeapAt = Date.now() + cooldownMs;
      }, LEAP_MS);
    }
  }, LEAP_CHARGE_MS);
  return true;
}

function enemyLeashRadius(npc) {
  const r = Number(npc.leashradius);
  return r > 0 ? r : ENEMY_LEASH;
}

function hasHome(npc) {
  return Number.isInteger(npc.homeX) && Number.isInteger(npc.homeY);
}

/** Make the enemy's current tile its home (first sight of it, or it lost the player). */
function anchorHere(npc) {
  npc.homeX = Math.floor(npc.position.x);
  npc.homeY = Math.floor(npc.position.y);
  npc.leash = { home: { x: npc.homeX, y: npc.homeY }, radius: enemyLeashRadius(npc) };
}

/** Stop chasing and settle where it stands. */
async function giveUpAndReanchor(npc, gridId) {
  npc.state = 'roam';
  npc.pursueTimerStart = null;
  npc.targetPC = null;
  npc.path = null;
  anchorHere(npc);
  await updateThisNPC.call(npc, gridId);
}

async function handleEnemyBehavior(gridId, TILE_SIZE) {
  //console.log(`🐺 NPC ${this.id} handling enemy behavior on grid ${gridId}.`);
  const tiles = GlobalGridStateTilesAndResources.getTiles();
  const resources = GlobalGridStateTilesAndResources.getResources();
  const npcs = Object.values(NPCsInGridManager.getNPCsInGrid(gridId) || {});
  // Single-PC store: the only PC on the grid is the local player (if present)
  const localPC = Object.values(playersInGridManager.getPlayersInGrid(gridId) || {})[0] || null;

  // First tick on this grid: the tile it stands on is its home (persisted with the state)
  const firstHome = !hasHome(this);
  if (firstHome) anchorHere(this);
  else if (!this.leash) this.leash = { home: { x: this.homeX, y: this.homeY }, radius: enemyLeashRadius(this) };

  // Force initial state to roam if not set
  if (!this.state || this.state === 'idle' || firstHome) {
    if (!this.state || this.state === 'idle') this.state = 'roam';
    await updateThisNPC.call(this, gridId);
  }
 
  if (this.leaping) return; // mid-leap (wind-up or flight): the leap's own timers finish it

  // Check whether the PC is visible at the start of any state (immediate reaction)
  const pcVisibleInRange = !!localPC &&
    localPC.hp > 0 &&
    getDistance(this.position, localPC.position) <= this.range &&
    canSeeTarget(this.position, localPC.position);

  // If we see the PC and we're not already pursuing/attacking, immediately react
  if (pcVisibleInRange && this.state === 'roam') {
    console.log(`⚡ NPC ${this.id} spotted PC ${localPC.username}! Entering pursue state.`);
    this.targetPC = localPC;
    this.state = 'pursue';
    this.pursueTimerStart = null;
    await updateThisNPC.call(this, gridId);
    return; // Skip the rest of the state processing
  }
 
  switch (this.state) {

    case 'idle': {
      // Idle is ONLY used to get unstuck - always transitions to roam, never back to idle
      this.pursueTimerStart = null; // Clear pursuit timer
      await this.handleIdleState(tiles, resources, npcs, 2, async () => {
        // After idle, always go to roam (never back to idle)
        this.state = 'roam';
        this.targetPC = null;
        await updateThisNPC.call(this, gridId);
      });
      break;
    }

    case 'pursue': {
      this.targetPC = refreshTarget(this.targetPC, localPC); // Refresh position from latest state
      if (!this.targetPC || this.targetPC.hp <= 0 || this.targetPC.iscamping) {
        await giveUpAndReanchor(this, gridId);
        break;
      }

      // Lost sight of the player: settle here (a new home, not the old one)
      if (!canSeeTarget(this.position, this.targetPC.position)) {
        await giveUpAndReanchor(this, gridId);
        break;
      }
      
      // Check if already in attack range AND can see target before pursuing
      const currentDistance = reach(this.position, this.targetPC.position);
      if (currentDistance <= this.attackrange) {
        // Verify a clear swing before switching to attack
        if (canHitTarget(this.position, this.targetPC.position)) {
          this.state = 'attack';
          await updateThisNPC.call(this, gridId);
          performAttack.call(this, gridId, TILE_SIZE); // no dead tick between arriving and swinging
          break;
        }
      }
      if (!this.pursueTimerStart) this.pursueTimerStart = Date.now();
      const timeSincePursueStart = Date.now() - this.pursueTimerStart;
      const distance = getDistance(this.position, this.targetPC?.position);
      // Give up if: 
      // 1. Target is too far AND we've been chasing for a while, OR
      // 2. We can't see the target anymore (behind wall)
      if (distance > this.range * 2 && timeSincePursueStart > 5000) {
        await giveUpAndReanchor(this, gridId); // the chase timed out: settle where it gave up
        break;
      }

      // Chase along an A* path (AllNPCsShared.followPath: the NPC's own terrain, no corner
      // cutting, never onto the player's or another NPC's tile), ending beside the player.
      // A single greedy step toward the player stalled whenever that one direction was
      // blocked (a rock on the diagonal's corner), leaving the enemy unable to reach a player
      // who could still hit it.
      const handlePursueWithLineOfSight = async () => {
        // Check if already in attack range AND can hit target BEFORE attempting to move
        const distanceToPlayer = reach(this.position, this.targetPC.position);
        if (distanceToPlayer <= this.attackrange) {
          if (canHitTarget(this.position, this.targetPC.position)) {
            this.state = 'attack';
            await updateThisNPC.call(this, gridId);
            performAttack.call(this, gridId, TILE_SIZE);
            return;
          }
        }

        // Far off and in the clear: the Ogre leaps to the player instead of walking
        if (tryLeap(this, gridId, tiles, resources, npcs)) return;

        const before = { x: Math.floor(this.position.x), y: Math.floor(this.position.y) };
        const result = this.followPath(
          Math.floor(this.targetPC.position.x), Math.floor(this.targetPC.position.y),
          tiles, resources, npcs, { stopShort: true }
        );
        const moved = Math.floor(this.position.x) !== before.x || Math.floor(this.position.y) !== before.y;
        if (moved) { this.stuckCounter = 0; return; }
        if (result === 'arrived') return; // beside the player, waiting on a clear swing

        // No route this tick (or none at all): after a few tries, wander to get unstuck
        if (!this.stuckCounter) this.stuckCounter = 0;
        this.stuckCounter++;
        if (result === 'blocked' || this.stuckCounter >= 3) {
          console.log(`NPC ${this.id} has no route to ${this.targetPC?.username}. Using idle to unstick.`);
          this.state = 'idle';
          this.stuckCounter = 0;
          this.path = null;
          await updateThisNPC.call(this, gridId);
        }
      };

      await handlePursueWithLineOfSight();
      break;
    }

    case 'attack': {
      this.targetPC = refreshTarget(this.targetPC, localPC); // Refresh position from latest state

      if (!this.targetPC || this.targetPC.hp <= 0 || this.targetPC.iscamping) {
        await giveUpAndReanchor(this, gridId); // gone, dead or camping
        break;
      }
      const distanceToTarget = reach(this.position, this.targetPC.position);
      if (distanceToTarget > this.attackrange) {
        //console.log(`PC ${this.targetPC.username} moved out of attack range. Returning to 'pursue' state.`);
        this.state = 'pursue';
        await updateThisNPC.call(this, gridId); // Save after transition
        break;
      }
      
      // Check for a clear swing (walls, doors, trees) before attacking
      if (!canHitTarget(this.position, this.targetPC.position)) {
        this.state = 'pursue';
        await updateThisNPC.call(this, gridId);
        break;
      }
 
      performAttack.call(this, gridId, TILE_SIZE);
      break;
    }

    case 'roam': {
      this.pursueTimerStart = null;
      const here = { x: Math.floor(this.position.x), y: Math.floor(this.position.y) };
      if (Math.max(Math.abs(here.x - this.leash.home.x), Math.abs(here.y - this.leash.home.y)) > this.leash.radius) {
        anchorHere(this); // drifted out (an unstick wander): this is home now
        await updateThisNPC.call(this, gridId);
      }
      await this.handleRoamState(tiles, resources, npcs, () => {
        // Don't change state - stay in roam
        // This callback is called after roam completes, but we just continue roaming
      });
      break;
    }

    default: {
      console.warn(`Unhandled state: ${this.state}`);
      break;
    }
  }
}

/**
 * Re-read the current target from the store so position/hp are fresh.
 * Returns null if the target is no longer the PC on this grid.
 */
function refreshTarget(targetPC, localPC) {
  if (!targetPC || !localPC) return null;
  return localPC.playerId === targetPC.playerId ? localPC : null;
}

/**
 * Calculates the Euclidean distance between two points (sight range).
 */
function getDistance(pos1, pos2) {
  return Math.sqrt((pos1.x - pos2.x) ** 2 + (pos1.y - pos2.y) ** 2);
}

/** Board reach (Chebyshev): the eight neighbours are all 1 away, like the player's swing. */
function reach(pos1, pos2) {
  return Math.max(Math.abs(pos1.x - pos2.x), Math.abs(pos1.y - pos2.y));
}

/**
 * The swing (docs/audits/combat-and-npc-review-2026-10-07.md, Track 1.6): a short wind-up on
 * the sprite, then the roll resolves, provided the enemy is still attacking a live target in
 * reach. Called on the tick the enemy enters `attack` and on every attack tick after.
 */
function performAttack(gridId, TILE_SIZE) {
  if (this.swingPending) return;
  this.swingPending = true;
  CombatFX.enemyWindup(this.id);
  CombatFX.engageEnemy(this.id, this.hp, this.maxhp);
  setTimeout(() => {
    this.swingPending = false;
    const target = this.targetPC;
    if (this.state !== 'attack' || !target || target.hp <= 0 || target.iscamping) return;
    // Re-read the live PC: it may have stepped away during the wind-up
    const live = Object.values(playersInGridManager.getPlayersInGrid(gridId) || {})[0];
    if (!live || live.playerId !== target.playerId) return;
    if (reach(this.position, live.position) > this.attackrange || !canHitTarget(this.position, live.position)) return;

    const attackRoll = Math.floor(Math.random() * 20) + 1;
    const isAHit = attackRoll + (this.attackbonus || 0) >= (live.armorclass || 0);
    const damage = isAHit ? Math.floor(Math.random() * 6) + 1 + (this.damage || 0) : 0;
    const land = () => {
      const pc = Object.values(playersInGridManager.getPlayersInGrid(gridId) || {})[0];
      if (!pc || pc.hp <= 0) return;
      if (!isAHit) { CombatFX.text(pc.position.x, pc.position.y, 'miss', 'miss'); return; }
      const newHP = Math.max(0, pc.hp - damage);
      CombatFX.playerHit();
      createImpactEffect(pc.position.x, pc.position.y, this.position.x, this.position.y);
      CombatFX.text(pc.position.x, pc.position.y, `-${damage}`, 'player');
      soundManager.playSFX('take_damage');
      playersInGridManager.updatePC(gridId, pc.playerId, { hp: newHP, lastUpdated: Date.now() });
      if (newHP <= 0) playersInGridManager.flushAfterTransaction(); // death is worth saving now
    };
    // A ranged enemy's shot flies to the player in its own look (resources.json `projectile`:
    // arrow, fireball, fireball-small, blob); `none` is a melee enemy with reach (the Ogre's 2):
    // its blow lands at once, as a melee blow beside the player does
    const shot = this.projectile || 'arrow';
    if (shot !== 'none' && reach(this.position, live.position) > 1) {
      CombatFX.projectile(this.position.x, this.position.y, live.position.x, live.position.y, land, shot);
    } else land();
  }, CombatFX.FX.WINDUP_MS);
}


export function attachEnemyBehavior(NPC) {
  NPC.prototype.handleEnemyBehavior = handleEnemyBehavior;
}